import { unavailableTotal, type InventoryRow } from "@estelle/domain";
import { ImageOff } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * The desktop and mobile row pieces `StockLevels` and `LowStock` render
 * through `DataTable`'s override points (#379) — split out the same way
 * `OrderRowParts.tsx` is (#350), so this stays a file `react-refresh` can
 * fast-refresh as a whole and the two screens never draw a variant, a
 * threshold or a low-stock warning two different ways.
 */

/**
 * Whether `row` has fallen to or below its threshold — the same comparison
 * `projectInventoryRow`'s `belowThreshold` makes (`projections.ts`), kept
 * here rather than imported from it so this file, like `OrderRowParts.tsx`,
 * computes off the row alone rather than off a projection built for a
 * different row type in a different screen.
 */
function isBelowThreshold(row: InventoryRow): boolean {
  return row.available <= row.effectiveLowStockThreshold;
}

/**
 * What the product cell draws: a product's title and photo, a variant's title
 * and SKU. An `InventoryRow` carries it, and so does a By warehouse row (E1,
 * #441) — the cell is the same one, so the two layouts never draw a product
 * two different ways.
 */
interface ProductLine {
  product: Pick<InventoryRow["product"], "title" | "featuredImageUrl">;
  variant: Pick<InventoryRow["variant"], "title" | "sku">;
}

/** "Blue / M · EL-1029" — drops either half a variant lacks (most SKUs are
 * blank on live data, per `fixtures/catalog.ts`). */
function variantAndSku(row: ProductLine): string {
  return [row.variant.title, row.variant.sku]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
}

/**
 * The product photo, straight from the URL the row already carries — no
 * fetch of any kind, per the ticket. A grey square with a broken-image glyph
 * stands in where a row has none, the same fallback `ProductList.tsx`'s own
 * `Thumbnail` uses, so a missing image reads the same way on both screens.
 */
function Thumbnail({ row }: { row: ProductLine }) {
  if (!row.product.featuredImageUrl) {
    return (
      <div
        className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded"
        aria-hidden
      >
        <ImageOff className="size-3.5" />
      </div>
    );
  }

  return (
    <img
      src={row.product.featuredImageUrl}
      alt=""
      loading="lazy"
      className="size-8 shrink-0 rounded object-cover"
    />
  );
}

/**
 * The desktop product cell (#379): one truncated line of title with the full
 * title as a tooltip, a muted truncated line of variant and SKU under it,
 * and the thumbnail beside both.
 *
 * The `max-w` on the outer flex box is load-bearing, not decorative: this
 * table has no `table-fixed` layout, so nothing else stops an auto-layout
 * column from growing to fit an untruncated title — which is the wrap this
 * ticket exists to fix. Capping the box is what gives `truncate` a width to
 * truncate against.
 */
export function ProductCell({ row }: { row: ProductLine }) {
  const subtitle = variantAndSku(row);

  return (
    <div className="flex max-w-[280px] items-center gap-2">
      <Thumbnail row={row} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium" title={row.product.title}>
          {row.product.title}
        </div>
        <div
          className="text-muted-foreground truncate text-xs"
          title={subtitle || undefined}
        >
          {subtitle || "—"}
        </div>
      </div>
    </div>
  );
}

/**
 * The "Available" figure, coloured with the same `text-warning` token
 * `InTransit.tsx` and `WarehouseList.tsx` use for their own at-risk states
 * (#379) — one colour for "needs attention" across the app, rather than a
 * different one per screen. Every row on `LowStock` qualifies by definition;
 * on `StockLevels` only the rows that have actually fallen to their
 * threshold do.
 */
export function AvailableCell({ row }: { row: InventoryRow }) {
  return (
    <span
      className={cn(
        "tabular-nums",
        // Below zero is wrong, not just low: shown red (#592).
        row.available < 0
          ? "text-destructive font-semibold"
          : isBelowThreshold(row) && "text-warning font-medium",
      )}
    >
      {row.available}
    </span>
  );
}

/**
 * One inventory row at a phone width (#379, #602): the product leads with a
 * two-line clamp, then the variant, SKU and warehouse, then the figures Shopify's
 * inventory screen shows in a small grid: Unavailable, Committed, Available, On
 * hand and In transit. Available is the one a warehouse operator on the floor
 * checks first, so it is the largest and, below zero, red.
 */
export function InventoryMobileCard({
  row,
  threshold,
}: {
  row: InventoryRow;
  /**
   * The threshold, and the control to change it (#487, #521) — a phone's card
   * has no column to put it in. Optional, so a card can be drawn without one.
   */
  threshold?: ReactNode;
}) {
  const subtitle = variantAndSku(row);
  const low = isBelowThreshold(row);
  const figures: { label: string; value: number; lead?: boolean }[] = [
    { label: "Unavailable", value: unavailableTotal(row.unavailable) },
    { label: "Committed", value: row.committed ?? 0 },
    { label: "Available", value: row.available, lead: true },
    { label: "On hand", value: row.onHand },
    { label: "In transit", value: row.inTransit },
  ];

  return (
    <div className="min-w-0">
      <div className="line-clamp-2 text-sm font-medium">
        {row.product.title}
      </div>
      {subtitle ? (
        <div className="text-muted-foreground mt-0.5 truncate text-sm">
          {subtitle}
        </div>
      ) : null}
      <div className="text-muted-foreground mt-0.5 truncate text-sm">
        {row.warehouse.code}
      </div>
      <dl className="mt-2 grid grid-cols-3 gap-x-4 gap-y-2">
        {figures.map((figure) => (
          <div key={figure.label}>
            <dt className="text-muted-foreground text-xs">{figure.label}</dt>
            <dd
              className={cn(
                "tabular-nums",
                figure.lead ? "text-base font-semibold" : "text-sm",
                figure.value < 0 && "text-destructive font-semibold",
                figure.lead && figure.value >= 0 && low && "text-warning",
              )}
            >
              {figure.value}
            </dd>
          </div>
        ))}
      </dl>
      {threshold ? (
        <div className="text-muted-foreground mt-1.5 flex items-center gap-1 text-sm">
          <span>Low at or below</span>
          {threshold}
        </div>
      ) : null}
    </div>
  );
}
